import {z} from 'zod';

import {FirestoreTimestampSchema} from '../types';

// ============================================================================
// Path segments
// ============================================================================

export const ASSIGNMENTS_ROOT_COLLECTION = 'assns' as const;
export const ASSIGNMENTS_COLLECTION = 'assignments' as const;
export const ASSIGNMENT_DOCS_SUBCOLLECTION = 'docs' as const;
export const FUNCTION_AUTOCOMPLETE_SETTINGS_DOCUMENT_ID =
  'autocompleteSettings' as const;

// ============================================================================
// Schemas
// ============================================================================

/**
 * Models currently supported by OpenAI's Realtime API.
 *
 * The `gpt-4o-*-realtime-preview*` family was retired by OpenAI after the GA
 * launch of `gpt-realtime` (2025-08-26) and `gpt-realtime-mini` (2025-10-03);
 * those names now return `model_not_found` at session/response time. Only the
 * GA `gpt-realtime` and `gpt-realtime-mini` aliases are accepted.
 */
export const SUPPORTED_REALTIME_MODELS = [
  'gpt-realtime',
  'gpt-realtime-mini',
] as const;

export const RealtimeModelSchema = z.enum(SUPPORTED_REALTIME_MODELS);
export type RealtimeModel = z.infer<typeof RealtimeModelSchema>;

export const FunctionAutocompleteSettingsDocumentSchema = z
  .object({
    enabled: z.boolean(),
    prompt: z.string(),
    model: RealtimeModelSchema,
    maxCompletionTokens: z.number(),
    updatedAt: FirestoreTimestampSchema.optional(),
  })
  .loose();

export type FunctionAutocompleteSettingsDocument = z.infer<
  typeof FunctionAutocompleteSettingsDocumentSchema
>;

export const DEFAULT_FUNCTION_AUTOCOMPLETE_SETTINGS: FunctionAutocompleteSettingsDocument =
  {
    enabled: false,
    prompt: '',
    model: 'gpt-realtime-mini',
    maxCompletionTokens: 4096,
  };

// ============================================================================
// Helpers
// ============================================================================

export function isSupportedRealtimeModel(
  model: unknown,
): model is RealtimeModel {
  return RealtimeModelSchema.safeParse(model).success;
}

export function isFunctionAutocompleteEnabled(
  settings: FunctionAutocompleteSettingsDocument | null | undefined,
): settings is FunctionAutocompleteSettingsDocument {
  return (
    !!settings &&
    settings.enabled &&
    typeof settings.prompt === 'string' &&
    settings.prompt.trim().length > 0 &&
    isSupportedRealtimeModel(settings.model) &&
    typeof settings.maxCompletionTokens === 'number' &&
    Number.isFinite(settings.maxCompletionTokens) &&
    settings.maxCompletionTokens > 0
  );
}

// ============================================================================
// Paths
// ============================================================================

export function getAssignmentDocumentPath({
  courseId,
  assignmentId,
}: {
  courseId: string;
  assignmentId: string;
}): string {
  return `${ASSIGNMENTS_ROOT_COLLECTION}/${courseId}/${ASSIGNMENTS_COLLECTION}/${assignmentId}`;
}

export function getFunctionAutocompleteSettingsDocumentPath({
  courseId,
  assignmentId,
}: {
  courseId: string;
  assignmentId: string;
}): string {
  return `${getAssignmentDocumentPath({
    courseId,
    assignmentId,
  })}/${ASSIGNMENT_DOCS_SUBCOLLECTION}/${FUNCTION_AUTOCOMPLETE_SETTINGS_DOCUMENT_ID}`;
}
